import { z } from 'zod';
import type { PillarApiRequestOptions } from './pillar-api-core';
import type { ActiveOrganizationSignatureSummary } from './legal-organization-signature-summary.types';
import { ActiveOrganizationSignatureSummarySchema } from './schemas/legal-organization-signature-summary.schemas';
import type {
    GetOrganizationLegalOverviewResponse,
    LegalHistorySearchRequest,
    SearchOrganizationAgreementSignaturesPageResponse,
} from './legal-organization-overview.types';
import {
    GetOrganizationLegalOverviewResponseSchema,
    LegalHistorySearchRequestSchema,
    SearchOrganizationAgreementSignaturesPageResponseSchema,
} from './schemas/legal-organization-overview.schemas';
import type { ListUpcomingUserAgreementsResponse } from './legal-upcoming-user-agreements.types';
import { ListUpcomingUserAgreementsResponseSchema } from './schemas/legal-upcoming-user-agreements.schemas';
import type { SearchLegalDocumentAssignmentsPageResponse } from './legal-documents.types';
import { SearchLegalDocumentAssignmentsPageResponseSchema } from './schemas/legal-documents.schemas';

type RequestWithSchema = <T>(
    endpoint: string,
    schema: z.ZodSchema<T>,
    options?: PillarApiRequestOptions
) => Promise<T>;

/** Read-only legal surfacing endpoints: signature prompts, notices and the Legal card. */
export function createLegalSurfacingApi(requestWithSchema: RequestWithSchema) {
    return {
        getActiveOrganizationSignatureSummary: () =>
            requestWithSchema<ActiveOrganizationSignatureSummary>(
                '/portal/v1/legal_overview/active/organization_signatures',
                ActiveOrganizationSignatureSummarySchema
            ),

        listUpcomingUserAgreements: () =>
            requestWithSchema<ListUpcomingUserAgreementsResponse>(
                '/portal/v1/legal_overview/active/upcoming_user_agreements',
                ListUpcomingUserAgreementsResponseSchema
            ),

        getOrganizationLegalOverview: (organizationUuid: string) =>
            requestWithSchema<GetOrganizationLegalOverviewResponse>(
                `/portal/v1/legal_overview/organizations/${z.string().uuid().parse(organizationUuid)}`,
                GetOrganizationLegalOverviewResponseSchema
            ),

        searchOrganizationAgreementSignaturesPage: (
            organizationUuid: string,
            params: LegalHistorySearchRequest = {}
        ) =>
            requestWithSchema<SearchOrganizationAgreementSignaturesPageResponse>(
                `/portal/v1/legal_overview/organizations/${z.string().uuid().parse(organizationUuid)}/signatures/search`,
                SearchOrganizationAgreementSignaturesPageResponseSchema,
                { method: 'POST', body: LegalHistorySearchRequestSchema.parse(params) }
            ),

        searchOrganizationPastAssignmentsPage: (
            organizationUuid: string,
            params: LegalHistorySearchRequest = {}
        ) =>
            requestWithSchema<SearchLegalDocumentAssignmentsPageResponse>(
                `/portal/v1/legal_overview/organizations/${z.string().uuid().parse(organizationUuid)}/past_assignments/search`,
                SearchLegalDocumentAssignmentsPageResponseSchema,
                { method: 'POST', body: LegalHistorySearchRequestSchema.parse(params) }
            ),
    };
}
